import { Component, HostListener, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { trigger, transition, animate, style } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { PractitionerService } from '../../services/practitioner/practitioner.service';
import { SidenavService } from '../../services/sidenav/sidenav.service';
import { PatientService } from '../../services/patient/patient.service';
import { HistoricalDataService } from '../../services/historical-data/historical-data.service';
import { AppointmentService } from '../../services/appointment/appointment.service';
import { CVANotificationService } from '../../services/cva-notification/cva-notification.service';
import { ConfirmationDialogService } from '../../shared/confirmation-dialog/services/confirmation-dialog.service';
import { SoapServiceService } from '../../services/soap-service/soap-service.service';
import { HistoricalDataComponent } from '../historical-data/historical-data.component';
import { AppointmentListComponent } from '../appointment-list/appointment-list.component';
import { HistoricalDataModalData, HistoricalDataNavItemCategory, HistoricalDataPreloadedPayload } from '../../models/historical-data.interface';
import { NotificationClassEnum } from '../../models/notification-class.enum';
import { CVA_AUTH_MODULES } from '../../models/auth.modules.enum';
import { IAppointment, AppointmentResponse } from '../../models/appointment.interface';
import { StartConsulationPatient } from '../../models/patient.interface';
import { PatientResponse } from '../../models/patient.interface';
import { AllergyResponse } from '../../models/allergy.interface';
import { VitalSigns } from '../../models/vital-signs.interface';
import { SoapNote, sectionName } from '../../models/dictation.interface';
import { Immunization } from '../../models/immunization.interface';
import { MedicationRequest } from '../../models/medical-request.interfcae';
import { Labhistory } from '../../models/labhistory';
import { ConditionResponse } from '../../models/condition.interface';
import { environment } from '../../../environments/environment';
import { CVA_CONSTANTS, MANUAL_REFRESH_IDENTIFIER, VIEW_MODE } from '../../util/constants';
import { AdditionalData, ManualResponse, RecordResponse } from '../../models/manual-refresh.interface';
import { ROUTE_CONSTANTS } from '../../util/route.constants';
import { Router } from '@angular/router';
import DOMPurify from 'dompurify';
import { getConsultationDisplayText } from '../../util/consultation-display.utils';
import { convertRtfToHtml } from '../../util/utils';
import { CdmHealthScreeningRow, CdmPlanRow, CdmPlanService } from '../../services/cdm-plan/cdm-plan.service';
import { lastValueFrom } from 'rxjs';

@Component({
    standalone: false,
    selector: 'cva-patient-dashboard',
    templateUrl: './patient-dashboard.component.html',
    styleUrl: './patient-dashboard.component.scss',
    animations: [
        trigger('slideInOut', [
            transition(':enter', [
                style({ width: 0, opacity: 0, marginLeft: 0, overflow: 'hidden' }),
                animate('320ms cubic-bezier(0.4, 0, 0.2, 1)', style({ width: '25%', opacity: 1, marginLeft: '8px' }))
            ]),
            transition(':leave', [
                style({ overflow: 'hidden' }),
                animate('260ms cubic-bezier(0.4, 0, 0.6, 1)', style({ width: 0, opacity: 0, marginLeft: 0 }))
            ])
        ])
    ],
    
})
export class PatientDashboardComponent implements OnInit, OnChanges {
  private static readonly DASHBOARD_RETURN_PATIENT_KEY = 'patientDashboardReturnPatientId';

  @Input() selectedPatientIdInput: string = '';
  @Input() refreshTrigger: number = 0;
  @Input() hideAppointmentsSection: boolean = false;
  @ViewChild(AppointmentListComponent) appointmentListComponent?: AppointmentListComponent;
  isAppointmentBookingOpen: boolean = false;
  practitionerId!: string;
  appointmentRefreshIdentifier = MANUAL_REFRESH_IDENTIFIER.PRACTITIONER_APPOINTMENT;
  selectedPatientId: string = '';
  patientData: PatientResponse | null = null;
  errorMessage: string = '';

  // Historical Data
  allergies: AllergyResponse[] = [];
  vitals: VitalSigns[] = [];
  consultationHistory: SoapNote[] = [];
  immunizations: Immunization[] = [];
  medications: MedicationRequest[] = [];
  labResults: Labhistory[] = [];
  conditions: ConditionResponse[] = [];

  groupedVitals: Array<{
    displayName: string;
    unit: string;
    latestValue: string;
    readings: { formattedDate: string; value: string }[];
  }> = [];
  selectedVitalTab: string = '';
  showMoreVitalTabs: boolean = false;

  get visibleTabsCount(): number {
    return 4;
  }

  loadingAllergies: boolean = false;
  loadingVitals: boolean = false;
  loadingConsultations: boolean = false;
  loadingImmunizations: boolean = false;
  loadingMedications: boolean = false;
  loadingLabResults: boolean = false;
  loadingConditions: boolean = false;

  hasAllergiesError: boolean = false;
  hasVitalsError: boolean = false;
  hasConsultationsError: boolean = false;
  hasImmunizationsError: boolean = false;
  hasMedicationsError: boolean = false;
  hasLabResultsError: boolean = false;
  hasConditionsError: boolean = false;

  currentHistoricalStartDate: string = '';
  currentHistoricalEndDate: string = '';

  selectedAllergyDetails: AllergyResponse | null = null;
  selectedImmunizationDetails: Immunization | null = null;
  selectedLabResultDetails: Labhistory | null = null;
  selectedMedicationDetails: MedicationRequest | null = null;
  selectedConditionDetails: ConditionResponse | null = null;
  selectedConsultationDetails: SoapNote | null = null;
  selectedLabResultEntries: Array<{ label: string; value: string }> = [];
  selectedConsultationEntries: Array<{ label: string; value: string }> = [];
  selectedConsultationDisplayText = '';
  consultationPreviewCache: Map<string, string> = new Map<string, string>();
  isCdmPlanLoading: boolean = false;
  cdmPlanError: string = '';
  cdmPlanResponse: unknown = null;
  cdmEligibility: string = '';
  cdmEligibilityReason: string = '';
  cdmAnnualSmartGoal: string = '—';
  cdmPlanRows: CdmPlanRow[] = [];
  cdmHealthScreeningRows: CdmHealthScreeningRow[] = [];
  loadingLabResultDetails: boolean = false;
  loadingConsultationDetails: boolean = false;
  activeAllergyUid: string | null = null;
  activeImmunizationUid: string | null = null;
  activeLabResultKey: string | null = null;
  activeMedicationKey: string | null = null;
  activeConsultationKey: string | null = null;
  activeConditionKey: string | null = null;
  tooltipPosition: { top: number | null; bottom: number | null; left: number; arrowLeft: number; isAbove: boolean } = { top: 0, bottom: null, left: 0, arrowLeft: 50, isAbove: true };
  consultationTooltipPosition: { top: number; left: number; arrowTopPx: number } = { top: 0, left: 0, arrowTopPx: 0 };
  private activeTooltipAnchor: HTMLElement | null = null;
  private activeConsultationTooltipAnchor: HTMLElement | null = null;

  get tooltipNgStyle(): Record<string, string | null> {
    return {
      top: this.tooltipPosition.top != null ? `${this.tooltipPosition.top}px` : null,
      bottom: this.tooltipPosition.bottom != null ? `${this.tooltipPosition.bottom}px` : null,
      left: `${this.tooltipPosition.left}px`,
    };
  }

  get consultationTooltipNgStyle(): Record<string, string> {
    return {
      top: `${this.consultationTooltipPosition.top}px`,
      left: `${this.consultationTooltipPosition.left}px`,
    };
  }

  get isCdmSourceLoading(): boolean {
    return this.loadingConsultations || this.loadingAllergies || this.loadingMedications;
  }

  tooltipType: 'allergy' | 'immunization' | 'lab' | 'medication' | 'consultation' | 'condition' | null = null;

  showAllergyTooltip(allergy: AllergyResponse, event: Event): void {
    this.selectedAllergyDetails = allergy;
    this.activeAllergyUid = allergy.uid;
    this.activeImmunizationUid = null;
    this.activeLabResultKey = null;
    this.activeMedicationKey = null;
    this.activeConsultationKey = null;
    this.activeConditionKey = null;
    this.selectedLabResultDetails = null;
    this.selectedMedicationDetails = null;
    this.selectedConditionDetails = null;
    this.selectedConsultationDetails = null;
    this.tooltipType = 'allergy';
    this.calculateTooltipPosition(event);
  }

  closeAllergyTooltip(): void {
    this.selectedAllergyDetails = null;
    this.activeAllergyUid = null;
    this.tooltipType = null;
  }

  showImmunizationTooltip(immunization: Immunization, event: Event): void {
    this.selectedImmunizationDetails = immunization;
    this.activeImmunizationUid = immunization.uid;
    this.activeAllergyUid = null;
    this.activeLabResultKey = null;
    this.activeMedicationKey = null;
    this.activeConsultationKey = null;
    this.activeConditionKey = null;
    this.selectedLabResultDetails = null;
    this.selectedMedicationDetails = null;
    this.selectedConditionDetails = null;
    this.selectedConsultationDetails = null;
    this.tooltipType = 'immunization';
    this.calculateTooltipPosition(event);
  }

  closeImmunizationTooltip(): void {
    this.selectedImmunizationDetails = null;
    this.activeImmunizationUid = null;
    this.tooltipType = null;
  }

  getLabResultKey(result: Labhistory): string {
    return `${result.displayName || ''}__${result.issuedOn || ''}__${result.appointmentId || ''}`;
  }

  async showLabResultTooltip(result: Labhistory, event: Event): Promise<void> {
    this.selectedLabResultDetails = result;
    this.activeLabResultKey = this.getLabResultKey(result);
    this.activeAllergyUid = null;
    this.activeImmunizationUid = null;
    this.activeMedicationKey = null;
    this.activeConsultationKey = null;
    this.activeConditionKey = null;
    this.selectedAllergyDetails = null;
    this.selectedImmunizationDetails = null;
    this.selectedMedicationDetails = null;
    this.selectedConditionDetails = null;
    this.selectedConsultationDetails = null;
    this.tooltipType = 'lab';
    this.loadingLabResultDetails = true;
    this.selectedLabResultEntries = [];
    this.calculateTooltipPosition(event);

    this.selectedLabResultEntries = await this.buildLabResultEntries(result.resultData);
    this.loadingLabResultDetails = false;
  }

  closeLabResultTooltip(): void {
    this.selectedLabResultDetails = null;
    this.selectedLabResultEntries = [];
    this.loadingLabResultDetails = false;
    this.activeLabResultKey = null;
    this.tooltipType = null;
  }

  getMedicationKey(medication: MedicationRequest): string {
    return `${medication.medicationText || ''}__${medication.authoredOn || ''}`;
  }

  getConditionKey(condition: ConditionResponse, index: number): string {
    return `${condition.uid || condition.id || ''}__${this.getConditionName(condition)}__${index}`;
  }

  getConditionName(condition: ConditionResponse): string {
    return this.formatTooltipValue(condition.condition);
  }

  getConditionStatus(condition: ConditionResponse): string {
    return this.formatTooltipValue(condition.status);
  }

  getConditionDate(condition: ConditionResponse): string {
    return this.formatTooltipDate(
      condition.onsetDateTime ||
      condition.recordedDate
    );
  }

  showConditionTooltip(condition: ConditionResponse, index: number, event: Event): void {
    this.selectedConditionDetails = condition;
    this.activeConditionKey = this.getConditionKey(condition, index);
    this.activeAllergyUid = null;
    this.activeImmunizationUid = null;
    this.activeLabResultKey = null;
    this.activeMedicationKey = null;
    this.activeConsultationKey = null;
    this.selectedAllergyDetails = null;
    this.selectedImmunizationDetails = null;
    this.selectedLabResultDetails = null;
    this.selectedMedicationDetails = null;
    this.selectedConsultationDetails = null;
    this.tooltipType = 'condition';
    this.calculateTooltipPosition(event);
  }

  closeConditionTooltip(): void {
    this.selectedConditionDetails = null;
    this.activeConditionKey = null;
    this.tooltipType = null;
  }

  showMedicationTooltip(medication: MedicationRequest, event: Event): void {
    this.selectedMedicationDetails = medication;
    this.activeMedicationKey = this.getMedicationKey(medication);
    this.activeAllergyUid = null;
    this.activeImmunizationUid = null;
    this.activeLabResultKey = null;
    this.activeConsultationKey = null;
    this.activeConditionKey = null;
    this.selectedAllergyDetails = null;
    this.selectedImmunizationDetails = null;
    this.selectedLabResultDetails = null;
    this.selectedConditionDetails = null;
    this.selectedConsultationDetails = null;
    this.tooltipType = 'medication';
    this.calculateTooltipPosition(event);
  }

  closeMedicationTooltip(): void {
    this.selectedMedicationDetails = null;
    this.activeMedicationKey = null;
    this.tooltipType = null;
  }

  getConsultationKey(consultation: SoapNote): string {
    return `${consultation.uid || ''}__${consultation.soapNoteDate || ''}__${consultation.appointmentId || ''}`;
  }

  async showConsultationTooltip(consultation: SoapNote, event: Event): Promise<void> {
    this.selectedConsultationDetails = consultation;
    this.activeConsultationKey = this.getConsultationKey(consultation);
    this.activeAllergyUid = null;
    this.activeImmunizationUid = null;
    this.activeLabResultKey = null;
    this.activeMedicationKey = null;
    this.activeConditionKey = null;
    this.selectedAllergyDetails = null;
    this.selectedImmunizationDetails = null;
    this.selectedLabResultDetails = null;
    this.selectedMedicationDetails = null;
    this.selectedConditionDetails = null;
    this.tooltipType = 'consultation';
    this.loadingConsultationDetails = true;
    this.selectedConsultationEntries = [];
    this.selectedConsultationDisplayText = '';
    this.calculateConsultationTooltipPosition(event);

    this.selectedConsultationEntries = await this.buildConsultationEntries(consultation);
    this.selectedConsultationDisplayText = getConsultationDisplayText(consultation);
    this.loadingConsultationDetails = false;
  }

  closeConsultationTooltip(): void {
    this.selectedConsultationDetails = null;
    this.selectedConsultationEntries = [];
    this.selectedConsultationDisplayText = '';
    this.loadingConsultationDetails = false;
    this.activeConsultationKey = null;
    this.tooltipType = null;
  }

  getMedicationInstruction(medication: MedicationRequest): string {
    const firstInstruction = medication.dosageInstructions?.[0];
    return this.formatTooltipValue(firstInstruction?.timingText);
  }

  getMedicationRoute(medication: MedicationRequest): string {
    const firstInstruction = medication.dosageInstructions?.[0];
    return this.formatTooltipValue(firstInstruction?.routeText);
  }

  getMedicationQuantity(medication: MedicationRequest): string {
    const quantity = medication.dispenseRequest?.quantity?.value;
    if (quantity === null || quantity === undefined) {
      return '—';
    }
    return String(quantity);
  }

  getMedicationDuration(medication: MedicationRequest): string {
    const duration = medication.dispenseRequest?.expectedSupplyDuration;
    if (!duration?.value) {
      return '—';
    }
    return `${duration.value} ${duration.unit || ''}`.trim();
  }

  getConsultationPreview(consultation: SoapNote): string {
    const key = this.getConsultationKey(consultation);
    return this.consultationPreviewCache.get(key) || 'Preparing summary...';
  }

  private async buildConsultationPreviewCache(consultations: SoapNote[]): Promise<void> {
    const previewEntries = await Promise.all(
      consultations.map(async (consultation) => {
        const preview = await this.buildConsultationPreview(consultation);
        return [this.getConsultationKey(consultation), preview] as const;
      })
    );

    this.consultationPreviewCache = new Map(previewEntries);
  }

  private async mapConsultationSectionsToSafeHtml(consultations: SoapNote[]): Promise<SoapNote[]> {
    const sectionKeys = [
      sectionName.subjective,
      sectionName.objective,
      sectionName.assessment,
      sectionName.plan,
    ];

    const converted = await Promise.all(
      consultations.map(async (consultation) => {
        const updated: SoapNote = { ...consultation };

        for (const sectionKey of sectionKeys) {
          const rawValue = updated[sectionKey];
          if (typeof rawValue !== 'string' || !rawValue.trim()) {
            continue;
          }

          const source = rawValue.trimStart();
          if (/^\{\\rtf/i.test(source)) {
            try {
              const htmlElements = await convertRtfToHtml(source);
              updated[sectionKey] = this.getSafeHTML(htmlElements);
            } catch {
              const fallback = DOMPurify.sanitize(rawValue);
              updated[sectionKey] = fallback;
            }
          } else {
            const sanitized = DOMPurify.sanitize(rawValue);
            updated[sectionKey] = sanitized;
          }
        }

        return updated;
      })
    );

    return converted;
  }

  private async buildConsultationPreview(consultation: SoapNote): Promise<string> {
    const sections: Array<{ value: unknown }> = [
      { value: consultation[sectionName.subjective] },
      { value: consultation[sectionName.objective] },
      { value: consultation[sectionName.assessment] },
      { value: consultation[sectionName.plan] },
    ];

    const previewParts: string[] = [];
    for (const section of sections) {
      const text = await this.toPlainSummaryText(section.value);
      if (text === '—') {
        continue;
      }
      previewParts.push(this.truncateText(text, 44));
      if (previewParts.length === 2) {
        break;
      }
    }

    return previewParts.length > 0 ? previewParts.join(' | ') : '—';
  }

  private async toPlainSummaryText(value: unknown): Promise<string> {
    let source = '';

    if (typeof value === 'string') {
      source = value;
    } else {
      return '—';
    }

    if (!source.trim()) {
      return '—';
    }

    if (/^\{\\rtf/i.test(source.trimStart())) {
      try {
        const htmlElements = await convertRtfToHtml(source.trimStart());
        const div = document.createElement('div');
        div.append(...htmlElements);
        const sanitizedDOM = DOMPurify.sanitize(div.innerHTML);
        return this.normalizePreviewText(sanitizedDOM);
      } catch {
        const sanitizedFallback = DOMPurify.sanitize(source);
        return this.normalizePreviewText(sanitizedFallback);
      }
    }

    const sanitizedDOM = DOMPurify.sanitize(source);
    return this.normalizePreviewText(sanitizedDOM);
  }

  private normalizePreviewText(value: string): string {
    const div = document.createElement('div');
    div.innerHTML = value;
    const normalized = (div.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    return normalized || '—';
  }

  private async buildConsultationEntries(consultation: SoapNote): Promise<Array<{ label: string; value: string }>> {
    const sectionConfig: Array<{ label: string; value: unknown }> = [
      { label: 'Subjective', value: consultation[sectionName.subjective] },
      { label: 'Objective', value: consultation[sectionName.objective] },
      { label: 'Assessment', value: consultation[sectionName.assessment] },
      { label: 'Plan', value: consultation[sectionName.plan] },
    ];

    const combinedText = sectionConfig
      .filter((entry) => typeof entry.value === 'string' && entry.value.trim())
      .map((entry) => this.toPlainPreview(entry.value))
      .join(' ');
    const embeddedEntries = this.parseEmbeddedSoapSections(combinedText);
    if (embeddedEntries.length > 1) {
      return embeddedEntries;
    }

    const entries = await Promise.all(
      sectionConfig.map(async (entry) => ({
        label: this.hasExplicitSectionLabel(entry.value, entry.label) ? entry.label : '',
        value: await this.formatConsultationSectionValue(entry.value, entry.label),
      }))
    );

    return entries.filter((entry) => entry.value !== '—');
  }

  private parseEmbeddedSoapSections(value: string): Array<{ label: string; value: string }> {
    const sectionPattern = /(SUBJECTIVE|OBJECTIVE|ASSESSMENT|PLAN|VITAL\s+SIGNS)\s*:\s*/gi;
    const matches = [...value.matchAll(sectionPattern)];
    if (matches.length < 2) {
      return [];
    }

    const entries: Array<{ label: string; value: string }> = [];
    const preamble = value.slice(0, matches[0].index ?? 0).trim();
    if (preamble) {
      entries.push({
        label: '',
        value: this.buildPlainConsultationHtml(preamble),
      });
    }

    entries.push(...matches.map((match, index) => {
      const start = (match.index ?? 0) + match[0].length;
      const end = index + 1 < matches.length ? (matches[index + 1].index ?? value.length) : value.length;
      const normalizedLabel = match[1].toLowerCase();
      const sectionValue = value.slice(start, end);
      const isVitalSigns = normalizedLabel.replace(/\s+/g, ' ') === 'vital signs';

      return {
        label: normalizedLabel
          .split(/\s+/)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        value: isVitalSigns
          ? this.buildVitalSignsHtml(`VITAL SIGNS: ${sectionValue}`, false)
          : this.buildPlainConsultationHtml(sectionValue),
      };
    }).filter((entry) => !!entry.value));

    return entries;
  }

  private hasExplicitSectionLabel(value: unknown, label: string): boolean {
    return typeof value === 'string'
      && new RegExp(`^\\s*${label}\\s*:`, 'i').test(this.toPlainPreview(value));
  }

  private async formatConsultationSectionValue(value: unknown, label: string): Promise<string> {
    if (typeof value !== 'string') {
      return this.toSafeLabHtml(value);
    }

    const plainText = this.toPlainPreview(value);
    const withoutDuplicateLabel = plainText.replace(new RegExp(`^${label}\\s*:\\s*`, 'i'), '');

    if (!/vital signs/i.test(plainText)) {
      return this.buildPlainConsultationHtml(withoutDuplicateLabel);
    }

    return this.buildVitalSignsHtml(withoutDuplicateLabel);
  }

  private buildPlainConsultationHtml(value: string): string {
    const normalized = value
      .split('•')
      .map((section) => section.trim())
      .join(' • ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!normalized) {
      return '';
    }

    const paragraph = document.createElement('p');
    paragraph.className = 'consultation-section-summary';
    paragraph.textContent = normalized;
    return DOMPurify.sanitize(paragraph.outerHTML);
  }

  private buildVitalSignsHtml(value: string, includeHeading = true): string {
    const vitalSectionMatch = /vital signs\s*:?:?\s*/i.exec(value);
    if (!vitalSectionMatch) {
      return DOMPurify.sanitize(value);
    }

    const sectionStart = (vitalSectionMatch.index ?? 0) + vitalSectionMatch[0].length;
    const vitalsText = value.slice(sectionStart).trim();
    const vitalLabelPattern = /(Alcohol Cessation(?:\s*\([^)]+\))?|Blood Pressure(?:\s*\([^)]+\))?|Body Mass Index(?:\s*\([^)]+\))?|Heart Rate(?:\s*\([^)]+\))?|Height(?:\s*\([^)]+\))?|Oxygen(?:\s*\([^)]+\))?|Respiratory Rate(?:\s*\([^)]+\))?|Smoking Cessation(?:\s*\([^)]+\))?|Temperature(?:\s*\([^)]+\))?|Weight(?:\s*\([^)]+\))?)\s*:/gi;
    const matches = [...vitalsText.matchAll(vitalLabelPattern)];

    if (matches.length === 0) {
      return DOMPurify.sanitize(value);
    }

    const root = document.createElement('div');
    const preamble = value.slice(0, vitalSectionMatch.index ?? 0).trim();

    if (preamble) {
      const preambleParagraph = document.createElement('p');
      preambleParagraph.className = 'consultation-vitals-preamble';
      preambleParagraph.textContent = preamble;
      root.append(preambleParagraph);
    }

    if (includeHeading) {
      const heading = document.createElement('div');
      heading.className = 'consultation-vitals-heading';
      heading.textContent = 'Vital Signs';
      root.append(heading);
    }

    const list = document.createElement('ul');
    list.className = 'consultation-vitals-list';

    matches.forEach((match, index) => {
      const label = match[1].trim();
      const itemStart = (match.index ?? 0) + match[0].length;
      const itemEnd = index + 1 < matches.length ? (matches[index + 1].index ?? vitalsText.length) : vitalsText.length;
      const rawValue = vitalsText.slice(itemStart, itemEnd).trim();

      const listItem = document.createElement('li');
      listItem.className = 'consultation-vitals-item';

      const labelSpan = document.createElement('span');
      labelSpan.className = 'consultation-vitals-label';
      labelSpan.textContent = `${label}:`;

      const valueSpan = document.createElement('span');
      valueSpan.className = 'consultation-vitals-value';
      valueSpan.textContent = rawValue || '—';

      listItem.append(labelSpan, valueSpan);
      list.append(listItem);
    });

    root.append(list);
    return DOMPurify.sanitize(root.innerHTML);
  }

  private toPlainPreview(value: unknown): string {
    let stripped = '';

    if (typeof value === 'string') {
      stripped = value;
    } else {
      return '—';
    }

    if (/^\{\\rtf/i.test(stripped)) {
      stripped = stripped
        .replace(/\{\\(?:fonttbl|colortbl|stylesheet|info|pict)[\s\S]*?\}/gi, ' ')
        .replace(/\\par[d]?\s?/gi, ' ')
        .replace(/\\tab\s?/gi, ' ')
        .replace(/\\'[0-9a-fA-F]{2}/g, '')
        .replace(/\\[a-zA-Z]+-?\d*\s?/g, ' ')
        .replace(/[{}]/g, ' ');
    }

    const div = document.createElement('div');
    div.innerHTML = DOMPurify.sanitize(stripped);
    stripped = (div.textContent || '').replace(/\s+/g, ' ').trim();

    if (!stripped) {
      return '—';
    }

    return stripped;
  }

  private truncateText(value: string, maxLength: number): string {
    if (value.length <= maxLength) {
      return value;
    }
    return `${value.slice(0, maxLength)}...`;
  }

  formatTooltipValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '—';
    }

    if (typeof value === 'object') {
      const maybeObject = value as { display?: string; code?: string; text?: string; value?: string };
      const normalized = maybeObject.display || maybeObject.code || maybeObject.text || maybeObject.value;
      if (normalized && normalized.trim()) {
        return normalized;
      }
      return '—';
    }

    const normalized = String(value).trim();
    return normalized || '—';
  }

  getAllergyStatusClass(): string {
    const allergy = this.selectedAllergyDetails as unknown as { clinicalStatus?: { code?: string } | string } | null;
    if (!allergy) {
      return '';
    }
    const rawStatus = typeof allergy.clinicalStatus === 'string'
      ? allergy.clinicalStatus
      : allergy.clinicalStatus?.code;
    return (rawStatus || '').toLowerCase();
  }

  getAllergyDetailValue(field: 'allergen' | 'category' | 'status' | 'type' | 'criticality' | 'verification' | 'reaction' | 'note'): string {
    const allergy = this.selectedAllergyDetails as unknown as {
      allergen?: { display?: string } | string;
      category?: string;
      clinicalStatus?: { code?: string } | string;
      type?: { code?: string } | string;
      criticality?: string;
      verificationStatus?: { code?: string } | string;
      reaction?: string;
      note?: string;
    } | null;

    if (!allergy) {
      return '—';
    }

    switch (field) {
      case 'allergen':
        return this.formatTooltipValue(typeof allergy.allergen === 'string' ? allergy.allergen : allergy.allergen?.display);
      case 'category':
        return this.formatTooltipValue(allergy.category);
      case 'status':
        return this.formatTooltipValue(typeof allergy.clinicalStatus === 'string' ? allergy.clinicalStatus : allergy.clinicalStatus?.code);
      case 'type':
        return this.formatTooltipValue(typeof allergy.type === 'string' ? allergy.type : allergy.type?.code);
      case 'criticality':
        return this.formatTooltipValue(allergy.criticality);
      case 'verification':
        return this.formatTooltipValue(typeof allergy.verificationStatus === 'string' ? allergy.verificationStatus : allergy.verificationStatus?.code);
      case 'reaction':
        return this.formatTooltipValue(allergy.reaction);
      case 'note':
        return this.formatTooltipValue(allergy.note);
      default:
        return '—';
    }
  }

  formatTooltipDate(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    const asString = String(value).trim();
    if (!asString) {
      return '—';
    }
    const parsed = new Date(asString);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }
    return this.formatTooltipValue(value);
  }

  getLabResultPreview(resultData: string): string {
    const parsed = this.parseLabResultData(resultData);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const firstEntry = Object.entries(parsed).find(([, value]) => value !== null && value !== undefined && String(value).trim() !== '');
      if (firstEntry) {
        return `${firstEntry[0]}: ${this.formatLabResultValue(firstEntry[1])}`;
      }
    }
    return this.formatLabResultValue(resultData);
  }

  private async buildLabResultEntries(resultData: string): Promise<Array<{ label: string; value: string }>> {
    const parsed = this.parseLabResultData(resultData);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Promise.all(
        Object.entries(parsed).map(async ([label, value]) => ({
          label,
          value: await this.toSafeLabHtml(value),
        }))
      );
    }

    return [{
      label: 'Result',
      value: await this.toSafeLabHtml(resultData),
    }];
  }

  private async toSafeLabHtml(value: unknown): Promise<string> {
    const normalized = this.formatTooltipValue(value);
    if (normalized === '—') {
      return '—';
    }

    if (/^\{\\rtf/i.test(normalized)) {
      try {
        const htmlElements = await convertRtfToHtml(normalized);
        return this.getSafeHTML(htmlElements);
      } catch {
        const sanitizedFallback = DOMPurify.sanitize(normalized);
        return sanitizedFallback;
      }
    }

    const sanitizedDOM = DOMPurify.sanitize(normalized);
    return sanitizedDOM;
  }

  getSafeHTML(htmlElements: HTMLElement[]): string {
    if (htmlElements && htmlElements.length > 0) {
      const div = document.createElement('div');
      div.append(...htmlElements);
      return DOMPurify.sanitize(div.innerHTML);
    }
    return '—';
  }

  private formatLabResultValue(value: unknown): string {
    const normalized = this.formatTooltipValue(value);
    if (normalized === '—') {
      return normalized;
    }

    if (/^\{\\rtf/i.test(normalized)) {
      return normalized
        .replace(/\\par[d]?\s?/gi, ' ')
        .replace(/\\tab\s?/gi, ' ')
        .replace(/\\'[0-9a-fA-F]{2}/g, '')
        .replace(/\\[a-zA-Z]+-?\d*\s?/g, '')
        .replace(/[{}]/g, '')
        .replace(/\s+/g, ' ')
        .trim() || '—';
    }

    return normalized;
  }

  private parseLabResultData(resultData: string): Record<string, unknown> | null {
    if (!resultData || typeof resultData !== 'string') {
      return null;
    }
    try {
      const parsed = JSON.parse(resultData);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }

  calculateConsultationTooltipPosition(event: Event): void {
    const element = (event.currentTarget as HTMLElement);
    this.activeConsultationTooltipAnchor = element;
    this.positionConsultationTooltip(element);
  }

  private positionConsultationTooltip(element: HTMLElement): void {
    const rect = element.getBoundingClientRect();
    const gap = 12;
    const viewportPadding = 16;
    const tooltipWidth = Math.min(520, Math.floor(window.innerWidth * 0.92));
    const estimatedHeight = Math.min(Math.floor(window.innerHeight * 0.64) + 80, 600);

    // Place tooltip to the LEFT of the clicked row
    const left = Math.max(viewportPadding, rect.left - tooltipWidth - gap);

    // Vertically centre on the row, clamped to viewport
    const rowCenterY = rect.top + rect.height / 2;
    let top = rowCenterY - estimatedHeight / 2;
    top = Math.max(viewportPadding, Math.min(top, window.innerHeight - estimatedHeight - viewportPadding));

    // Arrow top px inside the tooltip box (row centre relative to tooltip top)
    // Clamp so the arrow triangle stays fully visible (10px from edges)
    const arrowTopPx = Math.max(14, rowCenterY - top);

    this.consultationTooltipPosition = { top, left, arrowTopPx };
  }

  calculateTooltipPosition(event: Event): void {
    const element = (event.currentTarget as HTMLElement);
    this.activeTooltipAnchor = element;
    this.positionTooltip(element);
  }

  private positionTooltip(element: HTMLElement): void {
    const rect = element.getBoundingClientRect();
    const gap = 10;
    const viewportPadding = 16;
    const { tooltipWidth, tooltipHeight } = this.getTooltipDimensions();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const availableAbove = rect.top - gap - viewportPadding;
    const availableBelow = viewportHeight - rect.bottom - gap - viewportPadding;
    const canFitAbove = availableAbove >= tooltipHeight;
    const canFitBelow = availableBelow >= tooltipHeight;
    const isAbove = canFitAbove !== canFitBelow
      ? canFitAbove
      : availableAbove > availableBelow;

    const rowCenterX = rect.left + rect.width / 2;
    let left = rowCenterX - tooltipWidth / 2;
    const maxLeft = viewportWidth - tooltipWidth - viewportPadding;
    left = Math.max(viewportPadding, Math.min(left, maxLeft));

    let top: number | null = null;
    let bottom: number | null = null;
    if (isAbove) {
      bottom = viewportHeight - rect.top + gap;
      bottom = Math.max(viewportPadding, bottom);
    } else {
      top = rect.bottom + gap;
      top = Math.min(top, viewportHeight - viewportPadding);
    }

    // Arrow points back to the row center
    const tooltipCenterX = left + tooltipWidth / 2;
    const arrowOffsetX = ((rowCenterX - tooltipCenterX) / tooltipWidth) * 100;
    const arrowLeft = Math.max(10, Math.min(90, 50 + arrowOffsetX));

    this.tooltipPosition = { top, bottom, left, arrowLeft, isAbove };
  }

  @HostListener('window:resize')
  onViewportResize(): void {
    if (this.tooltipType === 'consultation' && this.activeConsultationTooltipAnchor?.isConnected) {
      this.positionConsultationTooltip(this.activeConsultationTooltipAnchor);
      return;
    }

    if (this.tooltipType && this.activeTooltipAnchor?.isConnected) {
      this.positionTooltip(this.activeTooltipAnchor);
    }
  }

  private getTooltipDimensions(): { tooltipWidth: number; tooltipHeight: number } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const defaultWidth = Math.min(304, Math.floor(viewportWidth * 0.9));
    const defaultHeight = 230;

    if (this.tooltipType === 'consultation') {
      return {
        tooltipWidth: Math.min(520, Math.floor(viewportWidth * 0.92)),
        tooltipHeight: Math.min(520, viewportHeight - 32),
      };
    }

    if (this.tooltipType === 'lab') {
      return {
        tooltipWidth: defaultWidth,
        tooltipHeight: Math.min(500, viewportHeight - 32),
      };
    }

    return { tooltipWidth: defaultWidth, tooltipHeight: defaultHeight };
  }

  cvaUI_url = environment.cvaUI_url;

  constructor(
    private readonly practitionerService: PractitionerService,
    private readonly sideNavService: SidenavService,
    private readonly patientService: PatientService,
    private readonly historicalDataService: HistoricalDataService,
    private readonly appointmentService: AppointmentService,
    private readonly cvaNotificationService: CVANotificationService,
    private readonly confirmationDialogService: ConfirmationDialogService,
    private readonly soapServiceService: SoapServiceService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly cdmPlanService: CdmPlanService,
  ) {
    const providerId = localStorage.getItem('providerId');
    this.practitionerId = providerId ?? '';
  }

  ngOnInit(): void {
    this.practitionerService.getPractitionerByHpiId(this.practitionerId).subscribe();

    // Keep selected patient only when returning from consultation launched via Patient Summary.
    if (!this.selectedPatientIdInput?.trim()) {
      const returnPatientId = this.consumeReturnPatientForDashboard();
      if (returnPatientId) {
        this.selectedPatientId = returnPatientId;
        this.loadPatientData(returnPatientId);
      } else {
        this.resetStandaloneDashboardState();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedPatientIdInput']?.currentValue?.trim() &&
      changes['selectedPatientIdInput']?.currentValue !== changes['selectedPatientIdInput']?.previousValue) {
      this.selectedPatientId = changes['selectedPatientIdInput'].currentValue;
      this.loadPatientData(changes['selectedPatientIdInput'].currentValue);
    }

    if (changes['refreshTrigger'] && !changes['refreshTrigger'].firstChange) {
      const patientIdToRefresh = (this.selectedPatientIdInput || this.selectedPatientId || '').trim();
      if (patientIdToRefresh) {
        this.selectedPatientId = patientIdToRefresh;
        this.loadPatientData(patientIdToRefresh);
      }
    }
  }

  onPatientSelected(patientId: string): void {
    const normalizedPatientId = (patientId || '').trim();
    if (normalizedPatientId) {
      this.selectedPatientId = normalizedPatientId;
      this.loadPatientData(normalizedPatientId);
    }
  }

  toggleAppointmentsSection(): void {
    this.hideAppointmentsSection = !this.hideAppointmentsSection;
  }

  get appointmentRefreshParams(): AdditionalData {
    return this.appointmentListComponent?.additionalParams ?? {};
  }

  refreshDashboardAppointments(data: ManualResponse<RecordResponse>): void {
    this.appointmentListComponent?.patchRefreshedData(data);
    this.appointmentListComponent?.fetchWalkinAppointment();
  }

  get showDashboardHeaderControls(): boolean {
    const currentUrl = this.router.url || '';
    const onConsultationRoute = currentUrl.includes(ROUTE_CONSTANTS.CONSULTATION);
    const onDashboardRoute =
      currentUrl.includes(ROUTE_CONSTANTS.DASHBOARD) ||
      currentUrl.includes(ROUTE_CONSTANTS.PATIENT_DASHBOARD);

    return onDashboardRoute && !onConsultationRoute;
  }

  get isConsultationRoute(): boolean {
    const currentUrl = this.router.url || '';
    return currentUrl.includes(ROUTE_CONSTANTS.CONSULTATION);
  }

  onAppointmentPatientSelected(patientMedicalRecordNumber: string): void {
    if (patientMedicalRecordNumber && patientMedicalRecordNumber.trim()) {
      this.selectedPatientId = patientMedicalRecordNumber;
      this.loadPatientData(patientMedicalRecordNumber);
    }
  }

  onBookingPanelStateChange(isOpen: boolean): void {
    this.isAppointmentBookingOpen = isOpen;
  }

  closeSummaryBookingPanel(): void {
    this.isAppointmentBookingOpen = false;
  }

  onSummaryAppointmentBooked(appointmentDate: Date): void {
    this.appointmentListComponent?.onAppointmentSchedule(appointmentDate);
    this.closeSummaryBookingPanel();
  }

  getDisplayPatientName(): string {
    if (!this.patientData) {
      return '';
    }

    const fullName = `${this.patientData.firstName || ''} ${this.patientData.lastName || ''}`
      .replace(/\bJH\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    return fullName || `${this.patientData.firstName || ''} ${this.patientData.lastName || ''}`.trim();
  }

  async startConsultation(): Promise<void> {
    const providerId = localStorage.getItem('providerId');
    const facilityCode = localStorage.getItem('facilityCode');
    const patient = this.patientData;

    if (!providerId || !patient?.uid) {
      this.cvaNotificationService.showNotification(
        CVA_CONSTANTS.MESSAGE.GENERATE_SOAP_NOTE_ERR,
        NotificationClassEnum.ERROR
      );
      return;
    }

    const shouldContinue = await this.confirmStartFreshConsultation();
    if (!shouldContinue) {
      return;
    }

    this.resetConsultationState();

    this.practitionerService.getPractitionerByHpiId(providerId).subscribe({
      next: (practitioner) => {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);

        this.appointmentService.getAllAppointmentsForPatient(patient.uid, practitioner.uid, start, end).subscribe({
          next: (res: { response: AppointmentResponse[] }) => {
            const activeAppointments = (res.response || []).filter(
              (appointment: AppointmentResponse) => appointment.status !== 'cancelled'
            );

            if (activeAppointments.length > 0) {
              const selectedAppointment = activeAppointments.sort((a, b) => a.start.localeCompare(b.start))[0] as unknown as IAppointment;
              const appointmentWithContext = {
                ...selectedAppointment,
                patient,
                practitioner,
                isFromPatientSearch: true,
              } as IAppointment;
              this.navigateToConsultation(appointmentWithContext, practitioner);
              return;
            }

            if (!facilityCode) {
              this.cvaNotificationService.showNotification(
                CVA_CONSTANTS.MESSAGE.GENERATE_SOAP_NOTE_ERR,
                NotificationClassEnum.ERROR
              );
              return;
            }

            const payload: StartConsulationPatient = {
              facilityCode,
              providerId: practitioner.uid,
              patientId: patient.uid,
            };

            this.appointmentService.createAppointmentForPatient(payload).subscribe({
              next: (result: { response: IAppointment }) => {
                const appointmentWithContext = {
                  ...result.response,
                  patient,
                  practitioner,
                  isFromPatientSearch: true,
                } as IAppointment;

                this.navigateToConsultation(appointmentWithContext, practitioner);
              },
              error: () => {
                this.cvaNotificationService.showNotification(
                  CVA_CONSTANTS.MESSAGE.GENERATE_SOAP_NOTE_ERR,
                  NotificationClassEnum.ERROR
                );
              },
            });
          },
          error: () => {
            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.GENERATE_SOAP_NOTE_ERR,
              NotificationClassEnum.ERROR
            );
          },
        });
      },
      error: () => {
        this.cvaNotificationService.showNotification(
          CVA_CONSTANTS.MESSAGE.GENERATE_SOAP_NOTE_ERR,
          NotificationClassEnum.ERROR
        );
      },
    });
  }

  private async confirmStartFreshConsultation(): Promise<boolean> {
    const savedConsultation = this.soapServiceService.getAppointmentAndDraftedNotes();
    const hasPendingNotes = !!savedConsultation && (
      Object.keys(savedConsultation.notes || {}).length > 0 ||
      (savedConsultation.transcription || '').trim().length > 0
    );

    if (!hasPendingNotes) {
      return true;
    }

    const confirmed = await lastValueFrom(
      this.confirmationDialogService.openConfirmDialog(CVA_CONSTANTS.MESSAGE.PENDING_NOTES_WARNING)
    );

    return confirmed === true;
  }

  private resetConsultationState(): void {
    this.soapServiceService.removeAppointmentAndDraftedNotes();
    this.soapServiceService.removeCurrentAppointment();
    localStorage.removeItem('currentAppointment');
  }

  private mapCdmResponseToTable(response: unknown): void {
    const mapped = this.cdmPlanService.mapResponseToTable(response);
    this.cdmEligibility = mapped.eligibility;
    this.cdmEligibilityReason = mapped.eligibilityReason;
    this.cdmAnnualSmartGoal = mapped.annualSmartGoal;
    this.cdmPlanRows = mapped.planRows;
    this.cdmHealthScreeningRows = mapped.healthScreeningRows;
  }

  openCdmModal(templateRef: TemplateRef<unknown>): void {
    if (this.isCdmPlanLoading) {
      return;
    }

    if (this.isCdmSourceLoading) {
      this.cvaNotificationService.showNotification(
        'Please wait. Historical data is still loading for CDM plan generation.',
        NotificationClassEnum.INFO
      );
      return;
    }

    this.isCdmPlanLoading = true;
    this.cdmPlanError = '';
    this.cdmPlanResponse = null;
    this.cdmEligibility = '';
    this.cdmEligibilityReason = '';
    this.cdmAnnualSmartGoal = '—';
    this.cdmPlanRows = [];
    this.cdmHealthScreeningRows = [];

    this.dialog.open(templateRef, {
      width: '920px',
      maxWidth: '96vw',
      maxHeight: '88vh',
      panelClass: 'custom-modalbox',
      autoFocus: false,
    });

    const payload = this.cdmPlanService.buildPayload({
      consultations: this.consultationHistory,
      allergies: this.allergies,
      medications: this.medications,
    }, '');
    this.historicalDataService.generateCdmPlan$(payload).subscribe({
      next: (response) => {
        this.cdmPlanResponse = response;
        this.mapCdmResponseToTable(response);
        this.isCdmPlanLoading = false;
      },
      error: () => {
        this.cdmPlanError = 'Failed to generate CDM plan. Please try again.';
        this.isCdmPlanLoading = false;
      },
    });
  }

  private navigateToConsultation(appointment: IAppointment, practitioner: unknown): void {
    this.sideNavService.patientId$ = appointment.patient.referenceId;
    this.sideNavService.patientMRN$ = appointment.patient.medicalRecordNumber;
    this.stashReturnPatientForDashboard(appointment.patient.medicalRecordNumber || this.selectedPatientId);

    this.router.navigate([ROUTE_CONSTANTS.CONSULTATION], {
      state: {
        appointment,
        viewMode: VIEW_MODE.TAB,
        practitioner,
      },
    });
  }

  loadPatientData(patientId: string): void {
    this.errorMessage = '';
    const normalizedPatientId = (patientId || '').trim();

    // Load patient details
    this.patientService.getPatientById(normalizedPatientId).subscribe({
      next: (response) => {
        const patient = response?.response;
        if (!patient) {
          this.setPatientLoadError();
          return;
        }

        this.patientData = patient;
        const historicalPatientId = patient.referenceId || normalizedPatientId;
        this.loadHistoricalData(historicalPatientId);
      },
      error: (error) => {
        console.error('Error loading patient data:', error);
        this.setPatientLoadError();
      }
    });
  }

  private setPatientLoadError(): void {
    this.patientData = null;
    this.errorMessage = 'Failed to load patient data';
  }

  private resetStandaloneDashboardState(): void {
    this.selectedPatientId = '';
    this.patientData = null;
    this.errorMessage = '';
    this.allergies = [];
    this.vitals = [];
    this.consultationHistory = [];
    this.immunizations = [];
    this.medications = [];
    this.labResults = [];
    this.conditions = [];
    this.groupedVitals = [];
    this.selectedVitalTab = '';
    localStorage.removeItem(PatientDashboardComponent.DASHBOARD_RETURN_PATIENT_KEY);
  }

  private stashReturnPatientForDashboard(patientId: string): void {
    const normalizedPatientId = (patientId || '').trim();
    if (!normalizedPatientId) {
      return;
    }
    localStorage.setItem(PatientDashboardComponent.DASHBOARD_RETURN_PATIENT_KEY, normalizedPatientId);
  }

  private consumeReturnPatientForDashboard(): string {
    const patientId = localStorage.getItem(PatientDashboardComponent.DASHBOARD_RETURN_PATIENT_KEY)?.trim() || '';
    if (patientId) {
      localStorage.removeItem(PatientDashboardComponent.DASHBOARD_RETURN_PATIENT_KEY);
    }
    return patientId;
  }

  loadHistoricalData(patientId: string): void {
    this.historicalDataService.resetDateRange();
    const { startDate, endDate } = this.historicalDataService.getNextRange();
    this.currentHistoricalStartDate = startDate;
    this.currentHistoricalEndDate = endDate;

    this.errorMessage = '';
    this.hasAllergiesError = false;
    this.hasVitalsError = false;
    this.hasConsultationsError = false;
    this.hasImmunizationsError = false;
    this.hasMedicationsError = false;
    this.hasLabResultsError = false;
    this.hasConditionsError = false;
    this.loadingAllergies = true;
    this.loadingVitals = true;
    this.loadingConsultations = true;
    this.loadingImmunizations = true;
    this.loadingMedications = true;
    this.loadingLabResults = true;
    this.loadingConditions = true;

    this.fetchAllergies(patientId);
    this.fetchVitals(patientId, startDate, endDate);
    this.fetchConsultations(patientId, startDate, endDate);
    this.fetchImmunizations(patientId);
    this.fetchMedications(patientId, startDate, endDate);
    this.fetchLabResults(patientId);
    this.fetchConditions(patientId, startDate, endDate);
  }

  private fetchAllergies(patientId: string): void {
    this.hasAllergiesError = false;
    this.loadingAllergies = true;
    this.historicalDataService.getAllergiesByPatientId$(patientId).subscribe({
      next: (data: AllergyResponse[]) => {
        this.allergies = data;
        this.loadingAllergies = false;
        this.updateHistoricalLoadError();
      },
      error: (error: unknown) => {
        console.error('Error loading allergies:', error);
        this.loadingAllergies = false;
        this.hasAllergiesError = true;
        this.updateHistoricalLoadError();
      }
    });
  }

  private fetchVitals(patientId: string, startDate: string, endDate: string): void {
    this.hasVitalsError = false;
    this.loadingVitals = true;
    this.historicalDataService.getVitalSignsForPatient$(patientId, startDate, endDate).subscribe({
      next: (data: VitalSigns[]) => {
        this.vitals = data;
        this.buildGroupedVitals();
        this.loadingVitals = false;
        this.updateHistoricalLoadError();
      },
      error: (error: unknown) => {
        console.error('Error loading vitals:', error);
        this.loadingVitals = false;
        this.hasVitalsError = true;
        this.updateHistoricalLoadError();
      }
    });
  }

  private fetchConsultations(patientId: string, startDate: string, endDate: string): void {
    this.hasConsultationsError = false;
    this.loadingConsultations = true;
    this.historicalDataService.getPreviousConsultationByPatientId$(
      patientId,
      startDate,
      endDate
    ).subscribe({
      next: (data: SoapNote[]) => {
        void (async () => {
          const convertedConsultations = await this.mapConsultationSectionsToSafeHtml(data);
          const sortedConsultations = [...convertedConsultations].sort((a, b) => {
            const dateA = new Date(a.soapNoteDate || 0).getTime();
            const dateB = new Date(b.soapNoteDate || 0).getTime();
            return dateB - dateA;
          });
          this.consultationHistory = sortedConsultations;
          this.consultationPreviewCache = new Map<string, string>();
          await this.buildConsultationPreviewCache(sortedConsultations);
          this.loadingConsultations = false;
          this.updateHistoricalLoadError();
        })();
      },
      error: (error: unknown) => {
        console.error('Error loading consultation history:', error);
        this.loadingConsultations = false;
        this.hasConsultationsError = true;
        this.updateHistoricalLoadError();
      }
    });
  }

  private fetchImmunizations(patientId: string): void {
    this.hasImmunizationsError = false;
    this.loadingImmunizations = true;
    this.historicalDataService.getImmunizationByPatientId$(patientId).subscribe({
      next: (data: Immunization[]) => {
        this.immunizations = data;
        this.loadingImmunizations = false;
        this.updateHistoricalLoadError();
      },
      error: (error: unknown) => {
        console.error('Error loading immunizations:', error);
        this.loadingImmunizations = false;
        this.hasImmunizationsError = true;
        this.updateHistoricalLoadError();
      }
    });
  }

  private fetchMedications(patientId: string, startDate: string, endDate: string): void {
    this.hasMedicationsError = false;
    this.loadingMedications = true;
    this.historicalDataService.getMedicationsForPatient$(patientId, startDate, endDate).subscribe({
      next: (data: MedicationRequest[]) => {
        this.medications = data;
        this.loadingMedications = false;
        this.updateHistoricalLoadError();
      },
      error: (error: unknown) => {
        console.error('Error loading medications:', error);
        this.loadingMedications = false;
        this.hasMedicationsError = true;
        this.updateHistoricalLoadError();
      }
    });
  }

  private fetchLabResults(patientId: string): void {
    this.hasLabResultsError = false;
    this.loadingLabResults = true;
    this.historicalDataService.getLabResults$(patientId).subscribe({
      next: (data: Labhistory[]) => {
        this.labResults = data;
        this.loadingLabResults = false;
        this.updateHistoricalLoadError();
      },
      error: (error: unknown) => {
        console.error('Error loading lab results:', error);
        this.loadingLabResults = false;
        this.hasLabResultsError = true;
        this.updateHistoricalLoadError();
      }
    });
  }

  private fetchConditions(patientId: string, startDate: string, endDate: string): void {
    this.hasConditionsError = false;
    this.loadingConditions = true;
    this.historicalDataService.getConditionsByPatientId$(patientId, startDate, endDate).subscribe({
      next: (data: ConditionResponse[]) => {
        this.conditions = [...data].sort((a, b) =>
          this.getConditionTimestamp(b) - this.getConditionTimestamp(a)
        );
        this.loadingConditions = false;
        this.updateHistoricalLoadError();
      },
      error: (error: unknown) => {
        console.error('Error loading conditions:', error);
        this.loadingConditions = false;
        this.hasConditionsError = true;
        this.updateHistoricalLoadError();
      }
    });
  }

  private updateHistoricalLoadError(): void {
    const isAnyRequestLoading = this.loadingAllergies
      || this.loadingVitals
      || this.loadingConsultations
      || this.loadingImmunizations
      || this.loadingMedications
      || this.loadingLabResults
      || this.loadingConditions;

    if (isAnyRequestLoading) {
      return;
    }

    const didAllRequestsFail = this.hasAllergiesError
      && this.hasVitalsError
      && this.hasConsultationsError
      && this.hasImmunizationsError
      && this.hasMedicationsError
      && this.hasLabResultsError
      && this.hasConditionsError;

    this.errorMessage = didAllRequestsFail ? 'Failed to load patient data' : '';
  }

  private getConditionTimestamp(condition: ConditionResponse): number {
    const timestamp = new Date(
      condition.recordedDate || condition.onsetDateTime || 0
    ).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private getCurrentHistoricalPatientId(): string {
    return this.patientData?.referenceId || this.selectedPatientId;
  }

  retryAllergies(): void {
    const patientId = this.getCurrentHistoricalPatientId();
    if (!patientId) {
      return;
    }
    this.fetchAllergies(patientId);
  }

  retryVitals(): void {
    const patientId = this.getCurrentHistoricalPatientId();
    if (!patientId || !this.currentHistoricalStartDate || !this.currentHistoricalEndDate) {
      return;
    }
    this.fetchVitals(patientId, this.currentHistoricalStartDate, this.currentHistoricalEndDate);
  }

  retryConsultations(): void {
    const patientId = this.getCurrentHistoricalPatientId();
    if (!patientId || !this.currentHistoricalStartDate || !this.currentHistoricalEndDate) {
      return;
    }
    this.fetchConsultations(patientId, this.currentHistoricalStartDate, this.currentHistoricalEndDate);
  }

  retryImmunizations(): void {
    const patientId = this.getCurrentHistoricalPatientId();
    if (!patientId) {
      return;
    }
    this.fetchImmunizations(patientId);
  }

  retryMedications(): void {
    const patientId = this.getCurrentHistoricalPatientId();
    if (!patientId || !this.currentHistoricalStartDate || !this.currentHistoricalEndDate) {
      return;
    }
    this.fetchMedications(patientId, this.currentHistoricalStartDate, this.currentHistoricalEndDate);
  }

  retryLabResults(): void {
    const patientId = this.getCurrentHistoricalPatientId();
    if (!patientId) {
      return;
    }
    this.fetchLabResults(patientId);
  }

  retryConditions(): void {
    const patientId = this.getCurrentHistoricalPatientId();
    if (!patientId || !this.currentHistoricalStartDate || !this.currentHistoricalEndDate) {
      return;
    }
    this.fetchConditions(patientId, this.currentHistoricalStartDate, this.currentHistoricalEndDate);
  }

  private buildGroupedVitals(): void {
    const map = new Map<string, { unit: string; readings: { ts: Date; formattedDate: string; value: string }[] }>();

    for (const vital of this.vitals) {
      const key = vital.displayName;
      if (!map.has(key)) {
        map.set(key, { unit: vital.displayUnit, readings: [] });
      }
      const entry = map.get(key)!;
      const ts = new Date(vital.timestamp);
      entry.readings.push({
        ts,
        formattedDate: ts.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
        value: `${vital.displayValue} ${vital.displayUnit}`.trim(),
      });
    }

    const isBloodPressure = (name: string): boolean => name.toLowerCase().replace(/\s+/g, '').includes('bloodpressure');

    this.groupedVitals = Array.from(map.entries())
      .map(([displayName, data]) => {
        data.readings.sort((a, b) => b.ts.getTime() - a.ts.getTime());
        return {
          displayName,
          unit: data.unit,
          latestValue: data.readings[0]?.value ?? '—',
          readings: data.readings,
        };
      })
      .sort((a, b) => {
        const aIsBp = isBloodPressure(a.displayName);
        const bIsBp = isBloodPressure(b.displayName);
        if (aIsBp && !bIsBp) {
          return -1;
        }
        if (!aIsBp && bIsBp) {
          return 1;
        }
        return a.displayName.localeCompare(b.displayName);
      });

    if (this.groupedVitals.length > 0) {
      const defaultBloodPressureTab = this.groupedVitals.find((vitalGroup) => isBloodPressure(vitalGroup.displayName));
      this.selectedVitalTab = defaultBloodPressureTab?.displayName ?? this.groupedVitals[0].displayName;
    }
  }

  selectVitalTab(name: string): void {
    this.selectedVitalTab = name;
    this.showMoreVitalTabs = false;
  }

  getVitalTabInitials(displayName: string): string {
    const parts = displayName.match(/[A-Za-z0-9]+/g) ?? [];

    if (parts.length === 0) {
      return displayName;
    }

    if (parts.length === 1) {
      return parts[0].slice(0, 3).toUpperCase();
    }

    return parts.map((part) => part[0]).join('').slice(0, 4).toUpperCase();
  }

  get isOverflowTabSelected(): boolean {
    const idx = this.groupedVitals.findIndex(g => g.displayName === this.selectedVitalTab);
    return idx >= this.visibleTabsCount;
  }

  get firstRowVitalTabs() {
    const all = this.groupedVitals;
    const n = this.visibleTabsCount;
    const selectedIdx = all.findIndex(g => g.displayName === this.selectedVitalTab);
    if (selectedIdx < n || selectedIdx === -1) {
      return all.slice(0, n);
    }
    return [...all.slice(0, n - 1), all[selectedIdx]];
  }

  get overflowVitalTabs() {
    const firstRowNames = new Set(this.firstRowVitalTabs.map(g => g.displayName));
    return this.groupedVitals.filter(g => !firstRowNames.has(g.displayName));
  }

  private getPreloadedModalData(category: HistoricalDataNavItemCategory): HistoricalDataPreloadedPayload {
    switch (category) {
      case 'Allergy':
        return { allergies: this.allergies };
      case 'Observations':
        return { vitals: this.vitals };
      case 'Consultations':
        return { consultations: this.consultationHistory };
      case 'Medications':
        return { medicationRequests: this.medications };
      case 'immunization':
        return { immunizations: this.immunizations };
      case 'Lab Results':
        return { labResults: this.labResults };
      case 'Conditions':
        return { conditions: this.conditions };
      default:
        return {};
    }
  }

  private getHistoricalAuthModule(category: HistoricalDataNavItemCategory): CVA_AUTH_MODULES {
    switch (category) {
      case 'Allergy':
        return CVA_AUTH_MODULES.CVA_ALLERGY;
      case 'Observations':
        return CVA_AUTH_MODULES.CVA_OBSERVATION;
      case 'Consultations':
        return CVA_AUTH_MODULES.CVA_SOAP_NOTES;
      case 'Medications':
        return CVA_AUTH_MODULES.CVA_MEDICATION_REQUEST;
      case 'immunization':
        return CVA_AUTH_MODULES.CVA_IMMUNIZATION;
      case 'Lab Results':
        return CVA_AUTH_MODULES.CVA_LAB_RESULTS;
      case 'Conditions':
        return CVA_AUTH_MODULES.CVA_PATIENT;
      default:
        return CVA_AUTH_MODULES.CVA_PATIENT;
    }
  }

  openHistoricalDetails(category: HistoricalDataNavItemCategory, title: string): void {
    const patientReferenceId = this.patientData?.referenceId;
    if (!patientReferenceId) {
      return;
    }

    const data: HistoricalDataModalData = {
      patientId: patientReferenceId,
      category,
      title,
      authModule: this.getHistoricalAuthModule(category),
      preloadedData: this.getPreloadedModalData(category),
    };

    this.dialog.open(HistoricalDataComponent, {
      maxHeight: '88vh',
      width: 'min(62vw, 1100px)',
      maxWidth: '96vw',
      data,
      panelClass: 'custom-modalbox',
    });
  }
}
